// Spor A block 3, T3.3 (XDIS-EPJ 7.1–7.9): state and checks of the XDIS18
// udskrivningsrapport form. The patient identifier and EpisodeOfCareIdentifier are not
// part of the form: Communicate takes them from the received XDIS16 (sourceExchangeId).
// Limits are the XSD's (Communicate xdis18-form); values above a limit are refused, not cut.

import { signedByFromPrefill, type SignedBy } from './xdisSignedBy';

export type { SignedBy };

export type DateTime = { date: string; time: string };
export type MedRow = { nameOfDrug: string; dosageForm: string; drugStrength: string; routeOfAdministration: string; latest: DateTime };
export type DiagnosisRow = { code: string; text: string };
export type Party = {
  ean: string;
  identifier: string;
  identifierCode: string;
  organisationName?: string;
  departmentName?: string;
  unitName?: string;
  streetName?: string;
  suburbName?: string;
  districtName?: string;
  postCodeIdentifier?: string;
};

export type Xdis18FormState = {
  receiver: Party;
  signedBy: SignedBy;
  admission: DateTime;
  endOfTreatment: DateTime;
  discharge: DateTime;
  causeOfAdmission: string;
  courseOfAdmission: string;
  riskOfContagion: string;
  diagnoses: DiagnosisRow[];
  cave: string;
  medicineComment: string;
  depot: MedRow[];
  pn: MedRow[];
  attachedExpire: string;
  attachedComment: string;
  receipt: boolean | null;
  pickUpOrDelivery: boolean | null;
  dosageExemptionReordered: boolean | null;
  futurePlans: string;
  /** Reused unchanged from the XDIS16 or the latest XDIS21 (7.2). */
  reused: Record<string, unknown>;
  /** 7.2: where the reused fields came from. */
  reuseSource: 'xdis21' | 'xdis16';
  carePlanExchangeId: string | null;
};

export const MEDICATION_LIMITS = { nameOfDrug: 35, dosageForm: 100, drugStrength: 70, routeOfAdministration: 35 } as const;
export const DIAGNOSIS_TEXT_MAX = 70;
const DATE = /^\d{4}-\d{2}-\d{2}$/;
const TIME = /^(([0-1][0-9])|(2[0-3])):[0-5][0-9]$/;

export const emptyDateTime = (): DateTime => ({ date: '', time: '' });
export const emptyMed = (): MedRow => ({ nameOfDrug: '', dosageForm: '', drugStrength: '', routeOfAdministration: '', latest: emptyDateTime() });

type Rec = Record<string, unknown>;
const rec = (v: unknown): Rec => (typeof v === 'object' && v !== null && !Array.isArray(v) ? (v as Rec) : {});
const s = (v: unknown): string => (typeof v === 'string' ? v : '');

function medsFrom(v: unknown): MedRow[] {
  return (Array.isArray(v) ? v : []).map(rec).map((m) => ({
    nameOfDrug: s(m.nameOfDrug),
    dosageForm: s(m.dosageForm),
    drugStrength: s(m.drugStrength),
    routeOfAdministration: s(m.routeOfAdministration),
    latest: { date: s(rec(m.latest).date), time: s(rec(m.latest).time) },
  }));
}

function dtFrom(v: unknown): DateTime {
  const o = rec(v);
  return { date: s(o.date), time: s(o.time) };
}

/** Only the exact XDIS18 keys — never expectedEnd / agreedDischarge from XDIS21. */
function actualDateTime(v: unknown): DateTime {
  if (v === undefined || v === null) return emptyDateTime();
  return dtFrom(v);
}

/** The form state from Communicate's xdis18-prefill (no CPR in it). */
export function stateFromPrefill(prefill: Rec): Xdis18FormState {
  const receiver = rec(prefill.receiver);
  const patient = rec(prefill.patient);
  return {
    receiver: {
      ean: s(receiver.ean),
      identifier: s(receiver.identifier),
      identifierCode: s(receiver.identifierCode) || 'kommunenummer',
      organisationName: s(receiver.organisationName) || undefined,
      departmentName: s(receiver.departmentName) || undefined,
      unitName: s(receiver.unitName) || undefined,
      streetName: s(receiver.streetName) || undefined,
      suburbName: s(receiver.suburbName) || undefined,
      districtName: s(receiver.districtName) || undefined,
      postCodeIdentifier: s(receiver.postCodeIdentifier) || undefined,
    },
    signedBy: signedByFromPrefill(prefill),
    admission: dtFrom(prefill.admission),
    endOfTreatment: actualDateTime(prefill.endOfTreatment),
    discharge: actualDateTime(prefill.discharge),
    causeOfAdmission: s(prefill.causeOfAdmission),
    courseOfAdmission: s(prefill.courseOfAdmission) || s(prefill.expectedCourseOfAdmission),
    riskOfContagion: s(prefill.riskOfContagion),
    diagnoses: [],
    cave: s(prefill.cave),
    medicineComment: s(prefill.medicineComment),
    depot: medsFrom(prefill.latestDepotMedication),
    pn: medsFrom(prefill.latestPnMedication),
    attachedExpire: '',
    attachedComment: '',
    receipt: null,
    pickUpOrDelivery: null,
    dosageExemptionReordered: null,
    futurePlans: '',
    reused: {
      patient,
      ...(prefill.practitioner ? { practitioner: prefill.practitioner } : {}),
      relatives: Array.isArray(prefill.relatives) ? prefill.relatives : [],
      ...(s(prefill.relativesComment) ? { relativesComment: s(prefill.relativesComment) } : {}),
    },
    reuseSource: /xdis21|carePlan|pleje/i.test(s(prefill.reuseSource) || s(prefill.reusedFrom) || s(prefill.source))
      ? 'xdis21'
      : 'xdis16',
    carePlanExchangeId: s(prefill.carePlanExchangeId) || null,
  };
}

/** XDIS-EPJ 7.6: PN medicine given in the 24 hours before discharge (local wall-clock). */
export function pnLast24h(rows: readonly MedRow[], discharge: DateTime): MedRow[] {
  if (!DATE.test(discharge.date) || !TIME.test(discharge.time)) return [];
  const toMin = (d: DateTime) => Date.parse(`${d.date}T${d.time}:00Z`) / 60000;
  const end = toMin(discharge);
  return rows.filter((m) => DATE.test(m.latest.date) && TIME.test(m.latest.time)).filter((m) => {
    const t = toMin(m.latest);
    return t <= end && t > end - 24 * 60;
  });
}

const medFilled = (m: MedRow) => [m.nameOfDrug, m.dosageForm, m.drugStrength, m.routeOfAdministration].some((v) => v.trim());

export type Issue = { field: string; message: string };

export function xdis18Issues(f: Xdis18FormState): Issue[] {
  const out: Issue[] = [];
  const dt = (v: DateTime, field: string, label: string) => {
    if (!DATE.test(v.date)) out.push({ field, message: `${label}: vælg en dato` });
    if (!TIME.test(v.time)) out.push({ field, message: `${label}: angiv klokkeslæt (TT:MM)` });
  };
  if (!/^\d{13}$/.test(f.receiver.ean.trim())) out.push({ field: 'receiver', message: 'Modtagerens lokationsnummer skal være 13 cifre' });
  if (!f.receiver.identifier.trim()) out.push({ field: 'receiver', message: 'Modtagerens identifikator mangler' });
  if (!f.signedBy.givenName.trim() || !f.signedBy.surname.trim() || !f.signedBy.title.trim()) {
    out.push({ field: 'signedBy', message: 'Underskriver: fornavn, efternavn og titel' });
  }
  dt(f.admission, 'admission', 'Indlæggelse');
  dt(f.endOfTreatment, 'endOfTreatment', 'Færdigbehandlet');
  dt(f.discharge, 'discharge', 'Udskrivning');
  if (!f.causeOfAdmission.trim()) out.push({ field: 'causeOfAdmission', message: 'Indlæggelsesårsag er påkrævet' });
  f.diagnoses.forEach((d, i) => {
    if (d.text.length > DIAGNOSIS_TEXT_MAX) out.push({ field: 'diagnoses', message: `Diagnose ${i + 1}: højst ${DIAGNOSIS_TEXT_MAX} tegn` });
  });
  const med = (rows: MedRow[], label: string, field: string) =>
    rows.filter(medFilled).forEach((m, i) => {
      for (const [k, max] of Object.entries(MEDICATION_LIMITS)) {
        const v = m[k as keyof typeof MEDICATION_LIMITS];
        if (v.trim().length > max) out.push({ field, message: `${label} ${i + 1}: ${k} højst ${max} tegn` });
      }
      if ((m.latest.date || m.latest.time) && (!DATE.test(m.latest.date) || !TIME.test(m.latest.time))) {
        out.push({ field, message: `${label} ${i + 1}: seneste givning skal have dato og klokkeslæt` });
      }
    });
  med(f.depot, 'Depotmedicin', 'depot');
  med(f.pn, 'PN-medicin', 'pn');
  return out;
}

const opt = (v: string) => (v.trim() ? v.trim() : undefined);
const medOut = (m: MedRow) => ({
  nameOfDrug: opt(m.nameOfDrug),
  dosageForm: opt(m.dosageForm),
  drugStrength: opt(m.drugStrength),
  routeOfAdministration: opt(m.routeOfAdministration),
  ...(DATE.test(m.latest.date) && TIME.test(m.latest.time) ? { latest: m.latest } : {}),
});

/** The report body for communicate-xdis build18 (Communicate ref-preview "xdis18"). */
export function xdis18Payload(f: Xdis18FormState, sender: Rec): Rec {
  const pn = pnLast24h(f.pn.filter(medFilled), f.discharge);
  const dm = {
    ...(opt(f.attachedExpire) || opt(f.attachedComment) ? { attached: { expire: opt(f.attachedExpire), comment: opt(f.attachedComment) } } : {}),
    ...(f.receipt !== null ? { receipt: f.receipt } : {}),
    ...(f.pickUpOrDelivery !== null ? { pickUpOrDelivery: f.pickUpOrDelivery } : {}),
    ...(f.dosageExemptionReordered !== null ? { dosageExemptionReordered: f.dosageExemptionReordered } : {}),
  };
  return {
    ...f.reused,
    sender: { ...sender, signedBy: f.signedBy },
    receiver: f.receiver,
    admission: f.admission,
    endOfTreatment: f.endOfTreatment,
    discharge: f.discharge,
    causeOfAdmission: f.causeOfAdmission,
    courseOfAdmission: opt(f.courseOfAdmission),
    riskOfContagion: opt(f.riskOfContagion),
    diagnoses: f.diagnoses.filter((d) => d.code.trim()).map((d) => ({ code: d.code.trim(), typeCode: 'SKSdiagnosekode', text: d.text })),
    cave: opt(f.cave),
    medicineComment: opt(f.medicineComment),
    latestDepotMedication: f.depot.filter(medFilled).map(medOut),
    latestPnMedication: pn.map(medOut),
    ...(Object.keys(dm).length > 0 ? { dischargeMedicine: dm } : {}),
    futurePlans: opt(f.futurePlans),
  };
}
